/**
 * ContentBlock Component
 * Renders a single content block (text, list, or tip)
 * Supports HTML content from TipTap editor
 */

import { ChevronRight, Lightbulb } from 'lucide-react';
import parse from 'html-react-parser';
import type { ContentBlock as ContentBlockType } from '../types';

interface ContentBlockProps {
  block: ContentBlockType;
}

// Helper to check if content is HTML
function isHtmlContent(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

// Render content - either as HTML (from TipTap) or plain text
function RenderContent({ content, className }: { content: string; className?: string }) {
  if (isHtmlContent(content)) {
    return (
      <div className={`prose prose-sm prose-invert max-w-none prose-p:text-white/80 prose-p:leading-relaxed prose-p:my-2 prose-headings:text-white prose-strong:text-white prose-a:text-pp-teal prose-li:text-white/80 ${className || ''}`}>
        {parse(content)}
      </div>
    );
  }
  return <p className={`text-white/80 leading-relaxed ${className || ''}`}>{content}</p>;
}

export function ContentBlock({ block }: ContentBlockProps) {
  switch (block.type) {
    case 'text':
      return (
        <div>
          <RenderContent content={block.content} />
        </div>
      );

    case 'list':
      return (
        <div>
          {block.title && (
            <h4 className="text-white font-medium mb-2">{block.title}</h4>
          )}
          <ul className="space-y-2">
            {block.items.map((item, index) => (
              <li key={index} className="flex items-center gap-2 text-white/80">
                <ChevronRight className="w-4 h-4 text-pp-orange shrink-0" />
                <span>{isHtmlContent(item) ? parse(item) : item}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'tip':
      return (
        <div className="bg-white/10 rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-pp-orange" />
            <h4 className="text-pp-orange font-medium">{block.title}</h4>
          </div>
          <RenderContent content={block.content} className="text-sm !text-white/75 prose-p:!text-white/75 prose-li:!text-white/75" />
        </div>
      );

    default:
      return null;
  }
}
